import { useCatalog, useHistory } from '../../api/queries';
import { Badge } from '@efira/ui';

import {
  coins as coinsWord,
  comments as commentsWord,
  gifts as giftsWord,
  viewers as viewersWord,
} from '../../lib/plural';
import { useLiveKind } from '../liveState';

/**
 * История эфиров.
 *
 * ЗАПИСЬ ЗАВОДИТСЯ ПРИ ЗАВЕРШЕНИИ ЭФИРА, а не при нажатии «выйти в эфир»:
 * запись без итога - это строка, которую нечем закрыть. Идущий сейчас эфир
 * в списке не показывается по той же причине.
 *
 * Смена аккаунта TikTok на ходу закрывает эфир и открывает следующий - в
 * истории это две записи, и так честно: зрители были разные.
 */
function when(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function History() {
  /* Место состояния эфира (единица 22): симулятор сюда не попадает. */
  const liveKind = useLiveKind();
  const history = useHistory();
  const catalog = useCatalog();

  const runs = history.data?.runs ?? [];
  const title = (gameId: string) =>
    catalog.data?.games.find((game) => game.id === gameId)?.title['ru'] ?? gameId;

  return (
    /* Имя пометки - словарём реестра (`history.modal`). */
    <div
      data-history="modal"
      data-live-place="history"
      data-live-state={liveKind}
      className="flex flex-col gap-4"
    >
      {history.data === undefined ? (
        <p className="text-foreground/80 text-sm">Загружаем…</p>
      ) : runs.length === 0 ? (
        <div
          data-history="empty"
          className="flex h-full min-h-40 flex-col items-center justify-center gap-1 text-center"
        >
          <p className="text-foreground text-base font-semibold">Эфиров пока не было</p>
          <p className="text-foreground/80 max-w-md text-sm">
            Первая запись появится, когда закончится эфир — или когда вы смените аккаунт TikTok на
            ходу: это закрывает текущую запись и открывает следующую. Симулятор записи не создаёт.
          </p>
        </div>
      ) : (
        <ul data-history="list" className="flex flex-col gap-2">
          {runs.map((run) => (
            <li
              key={run.id}
              data-history-run={run.id}
              className="border-foreground/10 rounded-lg @container flex flex-col gap-1 border p-3 text-sm"
            >
              <span className="flex flex-wrap items-center gap-2">
                <span className="text-foreground font-semibold">{when(run.startedAt)}</span>
                <span className="text-foreground/90 tabular-nums">{run.minutes} мин</span>
                {run.channel !== null && <span className="text-foreground/90">@{run.channel}</span>}
                {run.endReason !== null && <Badge variant="secondary">{run.endReason}</Badge>}
              </span>

              {/* Число и существительное согласуются: «комментариев: 5» -
                  это перевод наспех, а не сокращение (`lib/plural.ts`). */}
              <span className="text-foreground/90 flex flex-wrap gap-x-3 gap-y-1 text-xs tabular-nums">
                <span>{run.gameIds.map(title).join(', ') || 'игра не выбиралась'}</span>
                <span>{commentsWord(run.comments)}</span>
                <span>{giftsWord(run.gifts)}</span>
                <span>{coinsWord(run.giftCoins)}</span>
                <span>{viewersWord(run.viewers)}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
